import { createContext, useContext, useEffect, useState, type ReactNode } from 'react'
import { useQuery } from '@tanstack/react-query'
import { getCurrentUser, ApiError } from '../../api/client'
import type { UserContext } from '../../api/types'
import { identifyUser } from '../../lib/analytics'
import { useIdleTimeout } from './useIdleTimeout'
import { IdleWarningModal } from './IdleWarningModal'

// Mock auth: set VITE_MOCK_AUTH=true to auto-authenticate in dev.
// Default: false — uses real gateway authentication so the login page is visible.
const MOCK_AUTH = import.meta.env.VITE_MOCK_AUTH === 'true'

const MOCK_USER: UserContext = {
  id: 'dev-user-001',
  email: 'admin@defconai.local',
  displayName: 'Dev Admin',
  roles: ['admin', 'analyst', 'operator'],
  classification: { level: 'SECRET', caveats: [] },
  modules: [
    {
      name: 'example',
      displayName: 'Example Module',
      classificationCeiling: 'SECRET',
      customer: null,
      customerDisplayName: null,
    },
  ],
  // ADR-0069 D9 / F318 — exposed via /api/me for client-side nav gating.
  grants: ['modules/example:admin', 'modules/example:workbench:code-assistant'],
}

interface AuthContextValue {
  user: UserContext | null
  isLoading: boolean
  error: Error | null
  refetch: () => void
  logout: () => void
}

const AuthContext = createContext<AuthContextValue | null>(null)

/**
 * Auth provider that fetches user context from /api/me.
 * In dev mode with MOCK_AUTH, auto-authenticates with a mock user.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<UserContext | null>(MOCK_AUTH ? MOCK_USER : null)
  const [mockLoading, setMockLoading] = useState(MOCK_AUTH)

  const logout = async () => {
    // ADR-0019 T4: Server-side session termination.
    // Call the gateway logout endpoint to invalidate the httpOnly cookie
    // and clear the server-side session. Always clear local state even
    // if the API call fails (graceful degradation for offline/laptop mode).
    try {
      await fetch('/api/auth/logout', { method: 'POST', credentials: 'include' })
    } catch {
      // Network error — still clear local state
    }
    setUser(null)
    window.location.href = '/login'
  }

  // STIG V-222389: Listen for session-timeout events dispatched by fetchWithTimeout
  useEffect(() => {
    const onSessionTimeout = () => {
      setUser(null)
      window.location.href = '/login?reason=timeout'
    }
    window.addEventListener('session-timeout', onSessionTimeout)
    return () => window.removeEventListener('session-timeout', onSessionTimeout)
  }, [])

  // STIG V-222389: Idle timeout warning UI
  const {
    showWarning,
    secondsLeft,
    continueSession,
    enabled: idleTimeoutEnabled,
  } = useIdleTimeout()

  // Simulate brief loading in mock mode
  useEffect(() => {
    if (MOCK_AUTH) {
      const timer = setTimeout(() => setMockLoading(false), 100)
      return () => clearTimeout(timer)
    }
  }, [])

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['user'],
    queryFn: getCurrentUser,
    retry: false,
    staleTime: 5 * 60 * 1000, // 5 minutes
    enabled: !MOCK_AUTH, // Skip real API call in mock mode
  })

  useEffect(() => {
    if (MOCK_AUTH) return
    if (data) {
      setUser(data)
      // Identify user for product analytics
      identifyUser(data.id, {
        email: data.email,
        displayName: data.displayName,
        roles: data.roles,
      })
    } else if (error instanceof ApiError && error.status === 401) {
      setUser(null)
    }
  }, [data, error])

  return (
    <AuthContext.Provider
      value={{
        user,
        // Treat as still loading if the query finished with data but the user state
        // effect hasn't run yet — prevents a flash-to-login on page reload.
        isLoading: MOCK_AUTH ? mockLoading : isLoading || (data !== undefined && user === null),
        error: MOCK_AUTH ? null : (error as Error | null),
        refetch: MOCK_AUTH ? () => {} : refetch,
        logout,
      }}
    >
      {children}
      {idleTimeoutEnabled && showWarning && user && (
        <IdleWarningModal secondsLeft={secondsLeft} onContinue={continueSession} />
      )}
    </AuthContext.Provider>
  )
}

/**
 * Hook to access auth context.
 */
export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
